// =============================================================================
// FILE: src/pages/healthcare/patient/modalComponents/supplements/SupplementQuestionnaire.tsx
// =============================================================================

import React, { useState } from "react";
import { Modal, Button, Checkbox, Space, Divider, Typography, Alert } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";

const { Title, Text } = Typography;

export interface SupplementQuestionnaireAnswers {
	magnesium_forms: string[];
	methylation_base: string[];
	health_conditions: string[];
	stress_mood: string[];
	mood_type: string[];
	pain_supplement: string[];
}

interface Props {
	visible: boolean;
	onComplete: (answers: SupplementQuestionnaireAnswers) => void;
	onCancel: () => void;
}

const INITIAL: SupplementQuestionnaireAnswers = {
	magnesium_forms: [],
	methylation_base: [],
	health_conditions: [],
	stress_mood: [],
	mood_type: [],
	pain_supplement: [],
};

const SupplementQuestionnaire: React.FC<Props> = ({ visible, onComplete, onCancel }) => {
	const [answers, setAnswers] = useState<SupplementQuestionnaireAnswers>(INITIAL);

	const toggle = (cat: keyof SupplementQuestionnaireAnswers, val: string) =>
		setAnswers((prev) => {
			const cur = prev[cat];
			return {
				...prev,
				[cat]: cur.includes(val) ? cur.filter((v) => v !== val) : [...cur, val],
			};
		});

	const chk = (cat: keyof SupplementQuestionnaireAnswers, val: string) => answers[cat].includes(val);

	return (
		<Modal
			title={
				<Space>
					<QuestionCircleOutlined /> Supplement Recommendation Questionnaire
				</Space>
			}
			open={visible}
			onCancel={onCancel}
			width={800}
			footer={[
				<Button key="cancel" onClick={onCancel}>
					Cancel
				</Button>,
				<Button
					key="submit"
					type="primary"
					onClick={() => {
						onComplete(answers);
						setAnswers(INITIAL);
					}}
				>
					Generate Recommendations
				</Button>,
			]}
		>
			<Alert message="Answer the following to generate personalised supplement recommendations." type="info" showIcon style={{ marginBottom: 24 }} />
			<Space direction="vertical" style={{ width: "100%" }} size="large">
				<div>
					<Title level={5}>1. Which magnesium form for this patient?</Title>
					<Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
						Select based on patient symptoms
					</Text>
					<Space direction="vertical">
						<Checkbox checked={chk("magnesium_forms", "glycinate")} onChange={() => toggle("magnesium_forms", "glycinate")}>
							<strong>Magnesium Glycinate</strong> — Body tension, stress physiology, muscle cramps, twitching
						</Checkbox>
						<Checkbox checked={chk("magnesium_forms", "threonate")} onChange={() => toggle("magnesium_forms", "threonate")}>
							<strong>Magnesium L-Threonate</strong> — Brain fog, low recall, non-restorative sleep, cognitive fatigue
						</Checkbox>
						<Checkbox checked={chk("magnesium_forms", "none")} onChange={() => toggle("magnesium_forms", "none")}>
							<strong>None</strong>
						</Checkbox>
					</Space>
				</div>

				<Divider />

				<div>
					<Title level={5}>2. Base supplement from Super Methylation Report?</Title>
					<Space direction="vertical">
						{[
							["b-complex", "B Complex Plus"],
							["homocysteine", "Homocysteine Factors"],
							["atp360", "ATP360"],
							["none", "None"],
						].map(([v, l]) => (
							<Checkbox key={v} checked={chk("methylation_base", v)} onChange={() => toggle("methylation_base", v)}>
								<strong>{l}</strong>
							</Checkbox>
						))}
					</Space>
				</div>

				<Divider />

				<div>
					<Title level={5}>3. Does the client have any of the following?</Title>
					<Space direction="vertical">
						{[
							["high-bp", "High blood pressure"],
							["high-cholesterol", "Elevated cholesterol"],
							["high-glucose", "Elevated glucose"],
							["low-iron", "Low iron"],
							["low-mitoiq", "Low mitoIQ result"],
							["none", "None"],
						].map(([v, l]) => (
							<Checkbox key={v} checked={chk("health_conditions", v)} onChange={() => toggle("health_conditions", v)}>
								<strong>{l}</strong>
							</Checkbox>
						))}
					</Space>
				</div>

				<Divider />

				<div>
					<Title level={5}>4. Stress/mood supplements (if any)?</Title>
					<Space direction="vertical">
						<Checkbox checked={chk("stress_mood", "rapid-calm")} onChange={() => toggle("stress_mood", "rapid-calm")}>
							<strong>Rapid Calm</strong> — Acute stress (mental anxiety)
						</Checkbox>
						<Checkbox checked={chk("stress_mood", "calm-oil-acute")} onChange={() => toggle("stress_mood", "calm-oil-acute")}>
							<strong>MondaySunday Calm Oil</strong> — Acute stress (physical/somatic)
						</Checkbox>
						<Checkbox checked={chk("stress_mood", "calm-oil-chronic")} onChange={() => toggle("stress_mood", "calm-oil-chronic")}>
							<strong>MondaySunday Calm Oil</strong> — Chronic stress + physical symptoms
						</Checkbox>
						<Checkbox checked={chk("stress_mood", "mycare-calm")} onChange={() => toggle("stress_mood", "mycare-calm")}>
							<strong>MyCare Calm</strong> — Chronic stress + emotional (irritability/burnout)
						</Checkbox>
						<Checkbox checked={chk("stress_mood", "zenbiome-sleep")} onChange={() => toggle("stress_mood", "zenbiome-sleep")}>
							<strong>Zenbiome Sleep</strong> — Chronic stress + gut issues impacting sleep
						</Checkbox>
						<Checkbox checked={chk("stress_mood", "sleep-oil")} onChange={() => toggle("stress_mood", "sleep-oil")}>
							<strong>MondaySunday Sleep Oil</strong> — Racing thoughts impacting sleep
						</Checkbox>
						<Checkbox checked={chk("stress_mood", "none")} onChange={() => toggle("stress_mood", "none")}>
							<strong>None</strong>
						</Checkbox>
					</Space>
				</div>

				<Divider />

				<div>
					<Title level={5}>5. Mood presentation?</Title>
					<Space direction="vertical">
						<Checkbox checked={chk("mood_type", "5-htp")} onChange={() => toggle("mood_type", "5-htp")}>
							<strong>Low mood linked to serotonin</strong> — (low mood all the time, SAD, carb cravings)
						</Checkbox>
						<Checkbox checked={chk("mood_type", "stress-mood")} onChange={() => toggle("mood_type", "stress-mood")}>
							<strong>Low mood only under stress</strong>
						</Checkbox>
						<Checkbox checked={chk("mood_type", "none")} onChange={() => toggle("mood_type", "none")}>
							<strong>None</strong>
						</Checkbox>
					</Space>
				</div>

				<Divider />

				<div>
					<Title level={5}>6. Muscle/joint pain supplement (if any)?</Title>
					<Space direction="vertical">
						{[
							["mycare-repair", "MyCare Repair"],
							["pea", "PEA+"],
							["myomend", "Myomend"],
							["none", "None"],
						].map(([v, l]) => (
							<Checkbox key={v} checked={chk("pain_supplement", v)} onChange={() => toggle("pain_supplement", v)}>
								<strong>{l}</strong>
							</Checkbox>
						))}
					</Space>
				</div>
			</Space>
		</Modal>
	);
};

export default SupplementQuestionnaire;
